/**
 * `/sales/invoices/new` — composer route.
 *
 * Wraps the focused `ComposerPage` orchestrator in the workspace
 * shell. The composer owns its own layout (2-column with sticky
 * preview + sticky footer); the route just supplies breadcrumbs.
 *
 * @see prototype/sales/invoice-new.html — V1.1 baseline (S12)
 * @see docs/spec/11-new-invoice-screen-spec.md — section-by-section
 *      contract for the screen.
 */
import { createFileRoute, notFound } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { hasPermission } from "@flint/api-contract";
import { Alert } from "@flint/ui";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";
import { ComposerPage } from "../../../../features/invoices/index.ts";
import { meQueryOptions } from "../../../../lib/session.ts";

// Accept an optional `?customerId=<uuid>` so the customer detail page's
// "New invoice for X" affordance preselects the customer in the composer
// (CUSTOMERS-DETAIL-AC17). Unknown / malformed values are dropped rather
// than 404-ing — the composer falls back to the empty picker.
const searchSchema = z.object({
  customerId: z.string().uuid().optional().catch(undefined),
});

export const Route = createFileRoute("/(workspace)/sales/invoices/new")({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions).catch(() => null);
    const perms = me?.company?.permissions ?? [];
    if (!hasPermission(perms, "invoices.create")) {
      throw notFound();
    }
  },
  validateSearch: searchSchema,
  component: NewInvoiceRoute,
});

function NewInvoiceRoute() {
  const me = useQuery(meQueryOptions);
  const canCreate = hasPermission(me.data?.company?.permissions ?? [], "invoices.create");
  const { customerId } = Route.useSearch();

  return (
    <WorkspaceShell
      breadcrumbs={[
        { label: "Sales" },
        { label: "Invoices", to: "/sales/invoices" },
        { label: "New invoice" },
      ]}
      viewportClassName="!p-0"
      contentClassName="max-w-none"
    >
      {me.isLoading ? (
        <div className="p-6 text-sm text-muted">Checking invoice permissions...</div>
      ) : canCreate ? (
        <ComposerPage preselectCustomerId={customerId} />
      ) : (
        <div className="mx-auto max-w-3xl p-6">
          <Alert
            intent="bad"
            title="You don't have permission to create invoices"
            description="Ask an Owner or Admin to grant invoice draft access."
          />
        </div>
      )}
    </WorkspaceShell>
  );
}
